/**
 * Client Reports — domain types (CPT-1177/1178/1179).
 *
 * A report row is ONE region slot's self-contained HTML artifact, stored in S3
 * and served (permission-checked) by GET /api/analytics/client-reports/:id/content.
 * These types mirror the server row shape verbatim (camelCase via the
 * camelCaseResponse middleware) — see server/src/routes/clientReports.ts.
 *
 * ARCHITECTURAL INVARIANT: a report's HTML is NEVER carried on the row. The
 * client fetches exactly one region's bytes at a time, on demand. Holding every
 * region's HTML in memory at once is the presentation-only anti-pattern this
 * whole feature exists to close (see the CPT-1180 spike recommendation).
 */

/** Sentinel slot for reports with no region dimension. MUST match the server's
 *  DEFAULT_REGION_SLOT (server/src/lib/regionSplitValidation.ts). */
export const DEFAULT_REGION_SLOT = '__default__';

/** The combined-variant region key. MUST match the server's ALL_REGIONS_KEY. */
export const ALL_REGIONS_KEY = 'All Regions';

export interface ClientReport {
  id: string;
  /** Owning tenant code — a report belongs to exactly one tenant. */
  tenantCode: string;
  /** One browser upload → one group → one row per region slot. */
  uploadGroupId: string;
  /** This row's region, e.g. 'CO' / 'All Regions' / '__default__'. */
  regionSlot: string;
  /** 'live' = what clients see for this (tenant, region slot); at most one live
   *  row per slot (enforced by a partial unique index, migration 307). */
  status: import('./status').ReportStatus;
  title: string;
  /** Reporting-period bounds; null when the report carried no period meta. */
  periodStart: string | null;
  periodEnd: string | null;
  sourceFilename: string;
  s3Bucket: string;
  s3Key: string;
  /** Stored size of the HTML artifact — the client no longer holds the bytes. */
  byteSize: number;
  contentSha256: string;
  /** True when the artifact references relative assets (may not render fully). */
  hasRelativeAssetsWarning: boolean;
  currentVersion: number;
  /** Per-region-slot upload-generation counter (migration 312) — set once at
   *  insert time and immutable thereafter, distinct from currentVersion (a
   *  per-row edit-revision counter that keeps moving on PATCH/publish/etc). */
  regionVersion: number;
  active: boolean;
  uploadedAt: string;
  updatedAt: string;
  updatedBy: string | null;
  /**
   * Region slot → sibling report **ID** (never HTML), as returned by
   * GET /:id/regions. Optional pre-resolved tab set: the viewer reads the
   * endpoint itself and treats this only as an initial value, so the tab bar can
   * paint before the query resolves. Deliberately NOT html — see the
   * architectural invariant above.
   */
  regionFiles?: Record<string, string> | null;
  /** Report's native render width in px, for fit-to-width scaling (default 1440). */
  nativeWidth?: number | null;
  /** Toolbar dropdown enhancement at render time. Default ON — only an explicit
   *  false disables it. */
  enhancedDropdowns?: boolean | null;
  /** Manual sort position. Not served by the API today; kept for ordering.ts. */
  orderIndex?: number | null;
}

/**
 * ONE region slot's file inside an upload POST body. Matches
 * `regionFiles[slot]` in POST /api/analytics/client-reports.
 */
export interface RegionFileUpload {
  title: string;
  sourceFilename: string;
  html: string;
  hasRelativeAssetsWarning?: boolean;
  periodStart?: string;
  periodEnd?: string;
}

/** POST /api/analytics/client-reports body — one upload group. */
export interface ClientReportUploadPayload {
  tenantCode: string;
  /** Keyed by region slot; '__default__' for a report with no region dimension. */
  regionFiles: Record<string, RegionFileUpload>;
}

/**
 * Client-side UPLOAD STAGING shape (one extracted file, before it is split into
 * region slots). Never a persisted row — `html` lives here only until POST.
 */
export interface ClientReportInput {
  title: string;
  periodStart?: string;
  periodEnd?: string;
  sourceFilename: string;
  html: string;
  /**
   * Pre-split-filename fallback path (`<base>--<REGION>.html`, see
   * regionFileGrouping.ts): region key → that region's full HTML. Upload-time
   * only — it is consumed building the POST body and never reaches a row.
   */
  regionFiles?: Record<string, string> | null;
  hasRelativeAssetsWarning: boolean;
}

/** PATCH /api/analytics/client-reports/:id body — title is the only editable field. */
export interface ClientReportMetadataPatch {
  title: string;
}
